import React, { useContext, useState } from 'react'
import { VideoIcon, CalendarPlus, Upload } from 'lucide-react'
import { ProjectContext } from '@/context/ProjectContext'
import { AuthContext } from '@/context/AuthContext'
import { useWorkspace } from '@/context/WorkspaceContext'
import { useNavigate } from 'react-router-dom'
import { JoinLiveMeetingDialog } from '@/components/dashboard/home-new/JoinLiveMeetingDialog'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import Calendar from '@/assets/welcomedashboard/apps/calendar.png'
import Microsoft from '@/assets/welcomedashboard/apps/microsoft.png'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { ProjectLayout } from '@/components/dashboard/dialog/projectLayout'
import { ImportFiles } from '@/components/dashboard/dialog/importFiles'
import toast from 'react-hot-toast'

// Types
interface User {
  name: string
  integrations?: {
    googleCalendar?: boolean
    microsoftCalendar?: boolean
  }
}

export function AddMeetingCard() {
  const { project, setMeetings } = useContext(ProjectContext)
  const { user } = useContext(AuthContext) as { user: User }
  const { workspace } = useWorkspace()
  const navigate = useNavigate()
  const [showJoinLiveMeetingDialog, setShowJoinLiveMeetingDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const customFetch = useCustomFetch()

  const generateCalendarLink = async () => {
    if (user.integrations?.googleCalendar) {
      const baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE'
      const title = encodeURIComponent('New User Interview')
      const guests = encodeURIComponent(`invite+${project.stem}@usermuse.ai`)
      const eventLink = `${baseUrl}&text=${title}&add=${guests}`
      window.open(eventLink, '_blank')
    } else if (user.integrations?.microsoftCalendar) {
      const { isBusinessAccount, error } = await customFetch('/auth/isBusinessAccount')
      if (error) return toast.error('Failed to generate calendar link')
      const baseUrl = `https://outlook.${isBusinessAccount ? 'office' : 'live'}.com/calendar/0/deeplink/compose`
      const title = encodeURIComponent('New User Interview')
      const guests = encodeURIComponent(`invite+${project.stem}@usermuse.ai`)
      const eventLink = `${baseUrl}?subject=${title}&to=${guests}`
      window.open(eventLink, '_blank')
    } else {
      navigate('/dashboard/integrations')
    }
  }

  return (
    <div className="w-[360px] pb-[28px] border-b border-[#e4e4e4]">
      <p className="text-[#3f424a] text-[22px] font-bold font-['DM_Sans'] leading-relaxed">Add Meetings</p>
      <p className="mb-[16px] text-[#62708d] text-base font-medium font-['Manrope'] leading-[23px]">
        Schedule a new meeting with users or team members
      </p>
      
      <Button
        className="w-full bg-[#FF4A8E] hover:bg-[#E43D7A] mb-4 shadow-sm"
        onClick={() => setShowJoinLiveMeetingDialog(true)}
      >
        <VideoIcon size={16} className="mr-2" />
        Join Live Meeting
      </Button>
      
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="w-full bg-white hover:bg-gray-50 shadow-sm"
          onClick={generateCalendarLink}
        >
          {!user?.integrations?.googleCalendar && !user?.integrations?.microsoftCalendar ? (
            <>
              <CalendarPlus size={16} className="mr-2" />
              Connect Calendar
            </>
          ) : (
            <>
              <img
                src={user.integrations?.googleCalendar ? Calendar : Microsoft}
                className="w-4 h-4 mr-2"
                alt="Calendar"
              />
              Schedule
            </>
          )}
        </Button>
        <Button
          variant="outline"
          className="w-full bg-white hover:bg-gray-50 shadow-sm"
          onClick={() => setShowImportDialog(true)}
        >
          <Upload size={16} className="mr-2" />
          Upload
        </Button>
      </div>
      
      {showJoinLiveMeetingDialog && (
        <JoinLiveMeetingDialog handleClose={() => setShowJoinLiveMeetingDialog(false)} />
      )}

      {showImportDialog && (
        <ProjectLayout 
          importFile={true} 
          handleClose={() => setShowImportDialog(false)} 
        />
      )}
    </div>
  )
} 